import React, { useState } from "react";
import { AlertTriangle, BadgeDollarSign, Camera, Loader2, Send, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { fmtDate } from "../lib/dates";
import { Button } from "./ui/button";

export const EmailFlagIcon = ({ flag }) => flag ? (
  <span className="ml-1 inline-flex items-center gap-0.5 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold text-destructive" title={`${flag.kind}: ${flag.reason}`} data-testid="email-flag">
    <AlertTriangle size={10} /> {flag.kind}
  </span>
) : null;

export const FlaggedEmails = () => {
  const { data, reload } = useApi("/admin/email/flagged");
  const clear = async (id) => { try { await api.delete(`/admin/email/flagged/${id}`); toast.success("Flag cleared"); reload(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  if (!data?.length) return null;
  return (
    <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm" data-testid="email-flagged">
      <p className="flex items-center gap-2 font-semibold text-destructive"><AlertTriangle size={16} /> {data.length} member email(s) flagged — bounced, complained or undeliverable</p>
      <ul className="mt-2 space-y-1 text-xs">
        {data.map((f) => (
          <li key={f.id} className="flex items-center justify-between gap-2" data-testid={`flagged-${f.id}`}>
            <span><b>{f.name}</b> <span className="font-mono-accent text-muted-foreground">{f.email}</span> · {f.kind} · {fmtDate(f.at)}</span>
            <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={() => clear(f.id)} data-testid={`flag-clear-${f.id}`}><X size={12} /> Clear</Button>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-muted-foreground">Fix the address in Members &amp; Vetting (editing the email clears the flag automatically).</p>
    </div>
  );
};

export const PhotoNudgePanel = () => {
  const { data, reload } = useApi("/admin/photo-nudges/outstanding");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post("/admin/photo-nudges/run"); toast.success("Photo reminders are being sent"); setTimeout(reload, 4000); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="photo-nudge-panel">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><Camera size={18} className="text-gold" /> Akwaaba strip — missing photos</h3>
          <p className="text-xs text-muted-foreground">Members approved in the last {data.window_days} days without a profile photo. Reminded automatically with the dues run (max 2, 14 days apart).</p>
        </div>
        <Button size="sm" onClick={run} disabled={busy || data.items.length === 0} className="gap-1 bg-crimson" data-testid="photo-nudge-now">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Remind now</Button>
      </div>
      <ul className="mt-3 divide-y divide-gold/15 text-sm">
        {data.items.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 py-2" data-testid={`photo-row-${m.id}`}>
            <span><b>{m.name}</b> <span className="text-xs text-muted-foreground">approved {fmtDate(m.approved_at)}</span></span>
            <span className="font-mono-accent text-xs text-muted-foreground">{m.nudges ? `${m.nudges} reminder(s), last ${fmtDate(m.last_nudge)}` : "not yet reminded"}</span>
          </li>
        ))}
        {data.items.length === 0 && <li className="py-3 text-center text-xs text-palm" data-testid="photo-none">Every recent member has a photo.</li>}
      </ul>
    </div>
  );
};

export const FeeOutstanding = () => {
  const { data, reload } = useApi("/admin/fee-reminders/outstanding");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await api.post("/admin/fee-reminders/run"); toast.success("Fee reminders are being sent to eligible members"); setTimeout(reload, 4000); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!data) return null;
  return (
    <div className="glass rounded-2xl p-6" data-testid="fee-outstanding">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="flex items-center gap-2 font-serif-display text-xl font-bold text-crimson"><BadgeDollarSign size={18} className="text-gold" /> Registration fees outstanding</h3>
          <p className="text-xs text-muted-foreground">Members approved more than {data.grace_days} days ago are reminded automatically with the dues run (max 3 reminders, 14 days apart).</p>
        </div>
        <Button size="sm" onClick={run} disabled={busy || data.items.length === 0} className="gap-1 bg-crimson" data-testid="fee-remind-now">{busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Remind now</Button>
      </div>
      <ul className="mt-3 divide-y divide-gold/15 text-sm">
        {data.items.map((m) => (
          <li key={m.id} className="flex items-center justify-between gap-2 py-2" data-testid={`fee-row-${m.id}`}>
            <span><b>{m.name}</b> <span className="text-xs text-muted-foreground">approved {fmtDate(m.approved_at)} · paid ${m.paid_total.toFixed(0)} of ${m.fee.toFixed(0)}</span></span>
            <span className="font-mono-accent text-xs text-muted-foreground">{m.reminders ? `${m.reminders} reminder(s), last ${fmtDate(m.last_reminder)}` : "not yet reminded"}</span>
          </li>
        ))}
        {data.items.length === 0 && <li className="py-3 text-center text-xs text-palm" data-testid="fee-none">All approved members have paid their registration fee.</li>}
      </ul>
    </div>
  );
};
